import { apiGet, apiPost } from '../generic/Api_Functions'

/**
 * Boundary with Sun for the free-text translator (story #119).
 *
 * This module is the ONLY place that knows the route names and the wire field names of the
 * translation contract. Jupiter never talks to the translation service directly (RN14): every
 * call goes Jupiter -> Sun -> outbound adapter -> the external service. Keeping the boundary here
 * means that when Sun freezes its delivery contract, only this file changes.
 */

/** CU3 — global setting AND service health, already combined by Sun (RN1). */
export const fetchTranslationAvailability = async () => {
  const response = await apiGet('translation/availability')
  return response?.available === true
}

/**
 * CU2 — the language catalogue the service publishes (RN7).
 *
 * Sun answers `{ languages: [{ code, name, nativeName }] }` — three columns per language, not bare
 * codes, so that a language the service adds tomorrow shows up labelled without a code change in
 * either repo. The whole object is handed on: the dropdown needs a label, and `nativeName` can be
 * null, so the caller decides the fallback.
 *
 * @returns {Promise<Array<{ code: string, name: string, nativeName: ?string }>>}
 */
export const fetchSupportedLanguages = async () => {
  const response = await apiGet('translation/languages')
  const languages = Array.isArray(response) ? response : response?.languages
  return Array.isArray(languages) ? languages : []
}

/**
 * CU1 — asks Sun for a translation.
 *
 * Sun's edge is camelCase English, not the external service's snake_case: the third party's
 * vocabulary stops inside Sun's outbound adapter (RN14) so that a renamed field there never
 * reaches the browser.
 * `usarGlosario` is a fixed backend policy (RN12) and is not sent from here, and the response
 * carries no `terminos_protegidos` to ignore (RN13).
 *
 * @param {{ text: string, sourceLanguage: string, targetLanguage: string }} request
 * @param {AbortSignal} [signal] lets the caller cancel an in-flight translation (D-N2)
 * @returns {Promise<{ originalText: string, translatedText: string }>}
 */
export const requestTranslation = async ({ text, sourceLanguage, targetLanguage }, signal) => {
  const response = await apiPost('translation/translate', {
    text,
    sourceLanguage,
    targetLanguage
  }, {}, signal)

  return {
    originalText: response?.originalText ?? text,
    translatedText: response?.translatedText ?? ''
  }
}
